import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Popover } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  getMyReactionEmoji,
  getReactionUserId,
  groupReactions,
  idsMatch,
  isReactionVip,
} from "./chatReactionUtils";
import "./chatReactions.scss";

const POPUP_WIDTH = 260;

const getReactionUserName = (user) =>
  user?.userName || user?.user?.userName || user?.name || "User";

const getReactionAvatar = (user) =>
  user?.avatarUrl ||
  user?.profile_pic ||
  user?.user?.avatarUrl ||
  user?.user?.profile_pic ||
  "";

const getPopupPlacement = (rect) => {
  const spaceRight = window.innerWidth - rect.left;
  const spaceLeft = rect.right;
  return spaceRight >= POPUP_WIDTH || spaceRight >= spaceLeft
    ? "topLeft"
    : "topRight";
};

const ChatReactionBar = ({ reactions = [], currentUserId }) => {
  const { t } = useTranslation("common");
  const barRef = useRef(null);
  const [popup, setPopup] = useState(null);
  const groups = useMemo(() => groupReactions(reactions), [reactions]);
  const mine = getMyReactionEmoji(reactions, currentUserId);
  const activeGroup =
    groups.find((group) => group.emoji === popup?.emoji) || null;

  useEffect(() => {
    if (popup && !groups.some((group) => group.emoji === popup.emoji)) {
      setPopup(null);
    }
  }, [groups, popup]);

  useEffect(() => {
    if (!popup) return undefined;

    const onPointerDown = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (barRef.current?.contains(target)) return;
      if (target.closest(".chat-reaction-users-popover")) return;
      setPopup(null);
    };

    const onKeyDown = (event) => {
      if (event.key === "Escape") setPopup(null);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [popup]);

  if (!groups.length) return null;

  const usersPanel = activeGroup ? (
    <div className="chat-reaction-users-popover__panel">
      <div className="chat-reaction-users-popover__head">
        <span>
          {activeGroup.emoji} {activeGroup.count}
        </span>
        <button
          type="button"
          className="chat-reaction-users-popover__close"
          aria-label={t("chatMessages.close", "Close")}
          onClick={() => setPopup(null)}
        >
          <CloseOutlined />
        </button>
      </div>
      <ul className="chat-reaction-users-popover__list">
        {activeGroup.users.map((user, index) => {
          const name = getReactionUserName(user);
          const avatar = getReactionAvatar(user);
          const isMe = idsMatch(getReactionUserId(user), currentUserId);

          return (
            <li
              key={`${getReactionUserId(user) || index}`}
              className={`chat-reaction-users-popover__user${isReactionVip(user) ? " is-vip" : ""
                }`}
            >
              {avatar ? (
                <img src={avatar} alt="" />
              ) : (
                <span className="chat-reaction-users-popover__initial">
                  {name.charAt(0).toUpperCase()}
                </span>
              )}
              <span className="chat-reaction-users-popover__name">
                {name}
                {isMe ? (
                  <span className="chat-reaction-users-popover__you">
                    {t("chatMessages.you", "You")}
                  </span>
                ) : null}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  ) : null;

  return (
    <div className="chat-reaction-bar" ref={barRef}>
      {groups.map((group) => {
        const isMine = mine === group.emoji;
        const reactedByMe = group.users.some((user) =>
          idsMatch(getReactionUserId(user), currentUserId)
        );
        const isOpen = popup?.emoji === group.emoji;

        return (
          <Popover
            key={group.emoji}
            open={isOpen}
            trigger={[]}
            placement={isOpen ? popup.placement : "topLeft"}
            arrow={false}
            zIndex={1300}
            destroyTooltipOnHide
            getPopupContainer={() => document.body}
            overlayClassName="chat-reaction-users-popover"
            content={isOpen ? usersPanel : null}
          >
            <button
              type="button"
              className={`chat-reaction-chip${group.hasVip ? " is-vip" : ""}${reactedByMe || isMine ? " is-mine" : ""
                }`}
              onClick={(e) => {
                e.stopPropagation();
                if (isOpen) {
                  setPopup(null);
                  return;
                }
                setPopup({
                  emoji: group.emoji,
                  placement: getPopupPlacement(e.currentTarget.getBoundingClientRect()),
                });
              }}
              aria-label={`${group.emoji} ${group.count}`}
              aria-expanded={isOpen}
            >
              <span className="chat-reaction-chip__emoji">{group.emoji}</span>
              {group.count > 1 && (
                <span className="chat-reaction-chip__count">{group.count}</span>
              )}
            </button>
          </Popover>
        );
      })}
    </div>
  );
};

ChatReactionBar.propTypes = {
  reactions: PropTypes.array,
  currentUserId: PropTypes.string,
};

export default ChatReactionBar;
